import type { CSSProperties, ReactNode } from 'react';

interface FieldsetCardProps {
  title?: ReactNode;
  count?: number;
  actionIcon?: ReactNode;
  onAction?: () => void;
  actionTitle?: string;
  actionLabel?: string;
  rightContent?: ReactNode;
  shadow?: boolean | 'sm' | 'md' | 'lg';
  children?: ReactNode;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
}

/**
 * FieldsetCard - Card com título que interrompe a borda superior
 * Estilo visual inspirado em <fieldset><legend>
 *
 * Exemplo visual:
 *     ┌─── Título (5) ────────── [+] ─┐
 *     │                               │
 *     │  conteúdo do card             │
 *     │                               │
 *     └───────────────────────────────┘
 *
 * Props:
 * - title: string - Título à esquerda
 * - count: number - Quantidade de registros (exibido entre parênteses ao lado do título)
 * - actionIcon: ReactNode - Ícone customizado do botão de ação (default: Plus animado com gradiente)
 * - onAction: function - Callback do botão de ação (exibe botão quando fornecido)
 * - actionTitle: string - Tooltip do botão de ação
 * - actionLabel: string - Label visível do botão (default: 'Novo' quando usar ícone padrão)
 * - rightContent: ReactNode - Conteúdo customizado à direita (substitui actionIcon se ambos fornecidos)
 * - shadow: boolean | 'sm' | 'md' | 'lg' | false - Sombra do card (default: 'md')
 * - children: ReactNode - Conteúdo do card
 * - style: object - Estilos do container
 * - contentStyle: object - Estilos do conteúdo interno
 */
export default function FieldsetCard({
  title,
  count,
  actionIcon,
  onAction,
  actionTitle,
  actionLabel,
  rightContent,
  shadow = 'md',
  children,
  style = {},
  contentStyle = {}
}: FieldsetCardProps) {
  // Separar overflow do style para aplicar apenas no conteúdo
  const { overflow, overflowX, overflowY, ...containerStyle } = style;

  // Mapear valores de shadow para box-shadow
  const shadowStyles = {
    sm: '0 1px 2px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)',
    md: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)',
    lg: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)'
  };

  const getBoxShadow = () => {
    if (!shadow) return 'none';
    if (shadow === true) return shadowStyles.md; // default quando true
    return shadowStyles[shadow as 'sm' | 'md' | 'lg'] || shadowStyles.md;
  };

  // Aumentar padding superior quando tiver botão de ação
  const hasActionArea = !!(rightContent || actionIcon || onAction);
  const topPadding = hasActionArea ? '28px' : '20px';
  // Margem superior extra quando tem botão para não colar no componente acima
  const topMargin = hasActionArea ? '5px' : '0';

  return (
    <div style={{
      position: 'relative',
      border: '1px solid var(--panel-border)',
      borderRadius: '8px',
      padding: `${topPadding} 16px 16px`,
      marginTop: topMargin,
      marginBottom: '16px',
      background: 'white',
      boxShadow: getBoxShadow(),
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'visible', // SEMPRE visible para não cortar o título
      ...containerStyle
    }}>
      {/* Título que "interrompe" a borda (esquerda) */}
      {title && (
        <span style={{
          position: 'absolute',
          top: 0,
          left: '16px',
          transform: 'translateY(-50%)',
          background: 'white',
          padding: '0 8px',
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          {title}
          {typeof count === 'number' && (
            <span style={{ fontWeight: 400 }}>({count})</span>
          )}
        </span>
      )}

      {/* Conteúdo à direita (botão de ação ou conteúdo customizado) */}
      {(rightContent || actionIcon || onAction) && (
        <span style={{
          position: 'absolute',
          top: 0,
          right: '16px',
          transform: 'translateY(-50%)',
          background: 'white',
          padding: '0 4px',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center'
        }}>
          {rightContent || (
            <button
              className="btn-add plus-animated"
              onClick={onAction}
              title={actionTitle}
              style={{ height: '28px', padding: '0 10px', fontSize: '12px' }}
            >
              {actionIcon || (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <defs>
                    <linearGradient id="fcPlusG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#154680" />
                    </linearGradient>
                  </defs>
                  <g className="plus-h" stroke="url(#fcPlusG)"><path d="M5 12h14"/></g>
                  <g className="plus-v" stroke="url(#fcPlusG)"><path d="M12 5v14"/></g>
                </svg>
              )}
              {(actionLabel || !actionIcon) && (
                <span className="btn-text">{actionLabel || 'Novo'}</span>
              )}
            </button>
          )}
        </span>
      )}

      {/* Conteúdo - overflow aplicado aqui se necessário */}
      <div style={{
        ...(overflowX || overflowY
          ? { ...(overflowX && { overflowX }), ...(overflowY && { overflowY }) }
          : { overflow: overflow || 'visible' }),
        ...contentStyle
      }}>
        {children}
      </div>
    </div>
  );
}
